import { useEffect, useMemo, useRef, useState } from "react";

import type { UserDirectoryEntry, UserProfile } from "~/lib/matrix";

const DEBOUNCE_MS = 120;

export function isWellFormedMxid(term: string): boolean {
  return /^@[^:]+:.+$/.test(term.trim());
}

function localPartOf(mxid: string): string {
  const withoutLocal = mxid.slice(1);
  const at = withoutLocal.indexOf(":");
  return (at < 0 ? withoutLocal : withoutLocal.slice(0, at)) || mxid;
}

export type ResolveProfile = (mxid: string) => Promise<UserProfile | null>;

export interface DirectorySearchOptions {
  excludeUserIds?: readonly string[];
  offerMxid?: boolean;
  resolveProfile?: ResolveProfile;
}

export interface DirectorySearchResult {
  results: UserDirectoryEntry[];
  excludedResults: UserDirectoryEntry[];
  loading: boolean;
}

export function useDirectorySearch(
  onSearch: (term: string) => Promise<UserDirectoryEntry[]>,
  term: string,
  options: DirectorySearchOptions = {},
): DirectorySearchResult {
  const [rawResults, setRawResults] = useState<UserDirectoryEntry[]>([]);
  const [mxidFallback, setMxidFallback] = useState<UserDirectoryEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);
  const { excludeUserIds, resolveProfile, offerMxid } = options;
  const resolveProfileRef = useRef(resolveProfile);
  resolveProfileRef.current = resolveProfile;
  const offerMxidRef = useRef(offerMxid === true);
  offerMxidRef.current = offerMxid === true;
  const excludeKey = (excludeUserIds ?? []).join("\u0000");
  const trimmed = term.trim();

  useEffect(() => {
    if (trimmed.length === 0) {
      reqIdRef.current += 1;
      setRawResults([]);
      setMxidFallback(null);
      setLoading(false);
      return;
    }

    const id = ++reqIdRef.current;
    setLoading(true);
    setMxidFallback(null);

    const buildMxidFallback = (hasExactHit: boolean) => {
      if (id !== reqIdRef.current) return;
      if (!offerMxidRef.current || !isWellFormedMxid(trimmed) || hasExactHit) {
        setMxidFallback(null);
        return;
      }
      const base: UserDirectoryEntry = {
        userId: trimmed,
        displayName: localPartOf(trimmed),
        avatarUrl: null,
      };
      setMxidFallback(base);
      if (!resolveProfileRef.current) return;
      void resolveProfileRef
        .current(trimmed)
        .then((profile) => {
          if (id !== reqIdRef.current) return;
          if (!profile) return;
          setMxidFallback({
            userId: profile.userId || trimmed,
            displayName: profile.displayName?.length ? profile.displayName : localPartOf(trimmed),
            avatarUrl: profile.avatarUrl,
          });
        })
        .catch(() => undefined);
    };

    const handle = setTimeout(() => {
      void (async () => {
        try {
          const list = await onSearch(trimmed);
          if (id !== reqIdRef.current) return;
          setRawResults(list);
          buildMxidFallback(list.some((entry) => entry.userId === trimmed));
        } catch {
          if (id !== reqIdRef.current) return;
          setRawResults([]);
          buildMxidFallback(false);
        } finally {
          if (id === reqIdRef.current) setLoading(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [trimmed, onSearch]);

  const { results, excludedResults } = useMemo(() => {
    const pool = [...rawResults, ...(mxidFallback ? [mxidFallback] : [])];
    const excludeSet = new Set(excludeKey.length > 0 ? excludeKey.split("\u0000") : []);
    return {
      results: pool.filter((entry) => !excludeSet.has(entry.userId)),
      excludedResults: pool.filter((entry) => excludeSet.has(entry.userId)),
    };
  }, [rawResults, mxidFallback, excludeKey]);

  return { results, excludedResults, loading };
}
